import _ from 'lodash'
import { logException } from '../services/sentryLogger'

export const isLandscape = () => window.innerWidth > window.innerHeight;

export const isMobilePortrait = () => window.innerWidth < 768; // legacy
export const isMobileLandscape = () => window.innerHeight < 768;
export const isMobileAll = () => isLandscape() ? isMobileLandscape() : isMobilePortrait();

export function create_UUID() {
    var dt = new Date().getTime()
    var uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
        /[xy]/g,
        function (c) {
            var r = (dt + Math.random() * 16) % 16 | 0
            dt = Math.floor(dt / 16)
            return (c == 'x' ? r : (r & 0x3) | 0x8).toString(16)
        }
    )
    return uuid
}

export function buildDatesFromMMyy(selectedMonth: any, selectedYear: any) {
    const startDate: Date = new Date()
    startDate.setFullYear(selectedYear, selectedMonth - 1, 1)
    const endDate: Date = new Date()
    const nextMonth = selectedMonth % 12
    const nextYear = nextMonth === 0 ? selectedYear + 1 : selectedYear
    endDate.setFullYear(nextYear, nextMonth, 0) // 0 = last day of prev month
    return [startDate, endDate]
}

export const formatRatio = (n1: number, n2: number) =>
    n1 == 0 ? 0 : Number((n1 / n2) * 100.0).toFixed(4)

export const formatMillis = (n1: number) =>
    Number(Number(n1 / (3600 * 1000)).toFixed(4))

export const formatDateForStats = (date: any) => {
    let d = new Date(date),
        month = '' + (d.getMonth() + 1),
        day = '' + d.getDate(),
        year = d.getFullYear()
    if (month.length < 2) month = '0' + month
    if (day.length < 2) day = '0' + day
    return [year, month, day].join('-')
}

export function findClosestLowerIndex(
    nums: (number | undefined)[],
    target: number
): number {
    let closestIndex = -1
    let closestDiff = Infinity

    for (let i = 0; i < nums.length; i++) {
        let numi = nums[i]
        if (numi && numi < target) {
            const diff = target - numi
            if (diff < closestDiff) {
                closestDiff = diff
                closestIndex = i
            }
        }
    }

    return closestIndex
}
export function findClosestGreaterIndex(
    nums: (number | undefined)[],
    target: number
): number {
    let closestIndex = -1
    let closestDiff = Infinity

    for (let i = 0; i < nums.length; i++) {
        let numi = nums[i]
        if (numi && numi > target) {
            const diff = numi - target
            if (diff < closestDiff) {
                closestDiff = diff
                closestIndex = i
            }
        }
    }

    return closestIndex
}

// a little function to help us with reordering the result
export const reorder = <T,>(
    list: Iterable<T> | ArrayLike<T>,
    startIndex: number,
    endIndex: number
) => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)
    return result
}

export const prettifyRef = (ref: string) => {
    return ref.charAt(0).toUpperCase() + ref.slice(1).replaceAll('-', ' ')
}

export interface WithToastManager {
    toastManager: any
}

export function sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms))
}

export const enumNames = (ENUM: any) =>
    Object.keys(ENUM).filter((item) => {
        return isNaN(Number(item))
    })
export const enumValues = (ENUM: any) => Object.keys(ENUM)

export const firstLetterCapitalizer = (text: string | number = ''): string => {
    if (typeof text === 'number') {
        return `${text}`
    }

    text = text.trim()
    // @ts-ignore
    return (
        !!text &&
        typeof text === 'string' &&
        text.length > 0 &&
        text[0].toUpperCase() + text.slice(1) // toLowerCase()
    )
}

export const jsonListToCSV = (
    items: any[] = [],
    customHeader = Object.keys(items?.[0] ?? {}) ?? [] //FIXME better heuristic
) => {
    const replacer = (key: string, value: any) => (value === null ? '' : value) // specify how you want to handle null values here
    const csv = [
        customHeader.join(','), // header row first
        ...items.map((row) =>
            customHeader
                .map((fieldName) => {
                    const value = row[fieldName]
                    // FIXME better date heuristic
                    if (
                        fieldName?.includes('time') ||
                        fieldName?.includes('date')
                    ) {
                        return value.toString()
                    }
                    return JSON.stringify(value, replacer)
                })
                .join(',')
        ),
    ].join('\r\n')
    return csv
}

export const jsonArrayToCSV = (items: string[][], customHeader: string[]) => {
    const csv = [
        customHeader.join(','), // header row first
        ...items.map((row) => row.join(',')),
    ].join('\r\n')
    return csv
}

export function downloadCSV(filename: string, text: string) {
    const el = document.createElement('a')
    el.setAttribute(
        'href',
        'data:text/csv;charset=utf-8,' + encodeURIComponent(text)
    )
    el.setAttribute('download', filename)
    el.style.display = 'none'
    document.body.appendChild(el)
    el.click()
    document.body.removeChild(el)
}

/**
 * Return null if not a proper dateStr
 * @param dateStr
 */
export const dateStrToDate = (dateStr?: string) => {
    if (!dateStr) return null
    const time = Date.parse(dateStr)
    if (isNaN(time)) {
        logException('dateStrToDate', 'Impossible to parse dateStr='+dateStr)
        return null
    }
    return new Date(time)
}
export const dateStrToLocaleDateTime = (dateStr: string | null | undefined) =>
    !!dateStr && dateStrToDate(dateStr)?.toLocaleString()
export const dateStrToLocaleDate = (dateStr: string | null | undefined) =>
    !!dateStr && dateStrToDate(dateStr)?.toLocaleDateString()

// @ts-ignore
export const findStringEnumKeyName = (value?: string | null, enumType: any) => {
    for (let enumMember in enumType) {
        if (enumType[enumMember] === value) {
            return enumMember
        }
    }
    return null
}

const MILLIS_IN_DAY = 1000 * 3600 * 24
export const addDays = (date: Date | null | undefined, days: number) => {
    if (!date) {
        logException('addDays: ', 'Nil date input ...')
        return null
    }
    return new Date(date.getTime() + MILLIS_IN_DAY * days)
}

export const addMonths = (date: Date | null | undefined, months: number) =>
    date && new Date(date.setMonth(date.getMonth() + months))

export const diffDays = (date1?: Date | null, date2?: Date | null) => {
    if (!date1 || !date2) {
        logException('diffDays', 'Nil argument(s)', new Error('Nil argument(s)'));
        return NaN
    }
    // To calculate the time difference of two dates
    const Difference_In_Time = date2.getTime() - date1.getTime()

    // To calculate the no. of days between two dates
    return Math.ceil(Difference_In_Time / MILLIS_IN_DAY)
}

const flattenPropertiesInner = (
    acc: Record<string, any>,
    currentValue?: Object,
    basePath = '',
    skipEmpty = false
) => {
    if (_.isObject(currentValue)) {
        const pairs = _.toPairs(currentValue)
        pairs.forEach((p) =>
            flattenPropertiesInner(
                acc,
                p[1],
                (basePath ? basePath + '.' : '') + String(p[0])
            )
        )
    } else if (!skipEmpty || !_.isEmpty(currentValue)) {
        acc[basePath] = currentValue // leaf
    }
}

export const flattenProperties = (currentValue?: Object, skipEmpty = false) => {
    const result = {}
    flattenPropertiesInner(result, currentValue, '', skipEmpty)
    return result
}

export const extractTagContent = (
    str: string = '',
    openingTag: string,
    closingTag: string
) =>
    str?.substring(
        str.indexOf(openingTag) + openingTag.length,
        str.lastIndexOf(closingTag)
    )

export const array2str = (arr: (number | undefined)[]) =>
    arr
        .filter((n) => !!n)
        .sort() //FIXME change the arr !
        .join('')


export function levenshtein(a: string, b: string): number {
    const an = a ? a.length : 0
    const bn = b ? b.length : 0
    if (an === 0) {
        return bn
    }
    if (bn === 0) {
        return an
    }
    const matrix = new Array<number[]>(bn + 1)
    for (let i = 0; i <= bn; ++i) {
        let row = (matrix[i] = new Array<number>(an + 1))
        row[0] = i
    }
    const firstRow = matrix[0]
    for (let j = 1; j <= an; ++j) {
        firstRow[j] = j
    }
    for (let i = 1; i <= bn; ++i) {
        for (let j = 1; j <= an; ++j) {
            if (b.charAt(i - 1) === a.charAt(j - 1)) {
                matrix[i][j] = matrix[i - 1][j - 1]
            } else {
                matrix[i][j] =
                    Math.min(
                        matrix[i - 1][j - 1], // substitution
                        matrix[i][j - 1], // insertion
                        matrix[i - 1][j] // deletion
                    ) + 1
            }
        }
    }
    return matrix[bn][an]
}
// TODO
export const LOREM = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.';
export function titleCaseWord(word: string) {
    if (!word) return word
    return word[0].toUpperCase() + word.substr(1).toLowerCase() 
    // + (Math.random() > 0.5 ? LOREM : '')
}



export const formatTime = (time: number) => {
    const seconds = time % 60
    const minutes = Math.floor(time / 60)
    // const hours = Math.floor(time / 3600)
    // ${hours.toString().padStart(2, '0')}:
    return `${minutes.toString().padStart(2, '0')}:${seconds
        .toString()
        .padStart(2, '0')}`
}

export const addMinutesToCurrentTime = (minutesToAdd: number): string => {
    const now = new Date()
    now.setMinutes(now.getMinutes() + minutesToAdd)

    const hours = now.getHours().toString().padStart(2, '0')
    const minutes = now.getMinutes().toString().padStart(2, '0')

    return `${hours}:${minutes}`
}
export const addMinutesTo = (time: Date, minutesToAdd: number): string => {
    const now = new Date(time.getTime()) // FIXME was time alone
    now.setMinutes(now.getMinutes() + minutesToAdd)

    const hours = now.getHours().toString().padStart(2, '0')
    const minutes = now.getMinutes().toString().padStart(2, '0')

    return `${hours}:${minutes}`
}

export const diffSeconds = (date1: Date , date2: Date = new Date() ) => {

    // To calculate the time difference of two dates
    const Difference_In_Time = date2.getTime() - date1.getTime()

    // To calculate the no. of days between two dates
    const result = Math.ceil(Difference_In_Time / 1000)

    console.debug('diffSeconds', date2?.toLocaleTimeString(), '-', date1?.toLocaleTimeString(), '=', result);

    return result;
}




// export const array2enum = <T extends Readonly<TestT>>(arr: T) => {
//     const res = {} as EnumFromStrings<TestT>
//     arr.forEach((v) => {
//         res[v] = v
//     })
//     return res
// }
// const myEnum = array2enum(val)
// myEnum.a // ok
// myEnum.b // ok
// const my2nEnum = array2enum(['a', 'c'] as const);
// const dummy: Test = Test.a;



export const insertAt = <T>(arr: T[], index: number, newItem: T, fromIndex = 0) => {
    // Move all elements after location to create space
    for (let j = fromIndex; j > index; --j) {
        arr[j] = arr[j - 1]
    }
    arr[index] = newItem;
}
/**
 * [
    ...arr.slice(0, index),
    newItem,
    ...arr.slice(index),
]
 */
/**
 */ 

export type ENUM<T> = keyof T;

export const enumOf = <T extends string>(arr: readonly T[]) => {
  type S = (typeof arr)[number];
  /**
   * Prevents the modification of existing property attributes and values, and prevents the addition of new properties.
   * @param o Object on which to lock the attributes.
   */
  return Object.freeze(
    arr.reduce(
      (acc, k) => {
        acc[k] = k;
        return acc;
      },
      {} as Record<S, S>,
    ),
  );
};

export const arrayOf = <T>(n: number, fn: (i: number) => T) =>
    Array.from({ length: n }, (_, k) => fn(k))

export type ArrayOf<T, N extends number, R extends T[] = []> = 
  R['length'] extends N ? R : ArrayOf<T, N, [...R, T]>;